/**
 * Course Player Component
 * 
 * Soporta cuatro tipos de videos:
 * 1. YouTube (iframe HTML o URL) — IFrame API, controles nativos ocultos
 * 2. Videos HTML embebidos (iframe) — contenedor 16:9 responsive
 * 3. Videos MP4 — elemento <video> nativo
 * 4. Videos HLS (.m3u8) — HLS.js
 * 
 * IMPORTANTE: Para reproducir videos HLS, instala hls.js:
 * npm install hls.js
 * 
 * Luego agrega el script en angular.json o index.html:
 * "scripts": ["node_modules/hls.js/dist/hls.min.js"]
 */

import { Component, signal, OnInit, OnDestroy, inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { CoursesService, CursoCompleto, Modulo, Clase } from '../../services/courses.service';
import { NotificationService } from '../../services/notification.service';
import { InscripcionesService, ProgresoCurso } from '../../services/inscripciones.service';
import { ExamenesService, Examen } from '../../services/examenes.service';
import { AuthService } from '../../services/auth.service';
import { ReproduccionService } from '../../services/reproduccion.service';
import { GPTAssistantService, ChatMessage } from '../../services/gpt-assistant.service';
import { forkJoin, combineLatest, take } from 'rxjs';

// Importar HLS.js - usar import dinámico si es necesario
let Hls: any;

interface Lesson {
  id: number;
  title: string;
  duration: string;
  completed: boolean;
  videoUrl: string;
  audioUrl: string;
  tipo_contenido: string;
  contenido_blog: string | null;
  descripcion: string | null;
  foto_portada: string | null;
  recursos: RecursoLeccion[];
}

interface RecursoLeccion {
  id_recurso: number;
  nombre_recurso: string;
  tipo_recurso: string;
  url_recurso: string;
  tamaño_bytes: number;
  descripcion: string | null;
  orden: number;
  fecha_subida: string;
  id_usuario?: number; // Opcional: si existe, es un recurso subido por estudiante
}

interface CourseSection {
  id: number;
  title: string;
  lessons: Lesson[];
  expanded: boolean;
  examen?: {
    id: number;
    titulo: string;
    descripcion: string;
    tiempo_limite: number;
    intentos_permitidos: number;
    num_preguntas: number;
    nota_minima: string;
    estado: string;
  } | null;
}

interface Comment {
  id: number;
  user: string;
  avatar: string;
  comment: string;
  time: string;
  likes: number;
}

interface Resource {
  id: number;
  name: string;
  type: string;
  size: string;
  url: string;
}

@Component({
  selector: 'app-course-player',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './course-player.html',
  styleUrl: './course-player.css',
})
export class CoursePlayerComponent implements OnInit, OnDestroy, AfterViewInit {
  private static ytApiLoading: Promise<void> | null = null;
  private static readonly YOUTUBE_SEEK_SECONDS = 10;
  /** YouTube elige calidad según tamaño del iframe; mínimo recomendado para HD. */
  private static readonly YOUTUBE_PLAYER_WIDTH = 1280;
  private static readonly YOUTUBE_PLAYER_HEIGHT = 720;
  private static readonly YOUTUBE_BRANDING_SCALE = 1.12;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private coursesService = inject(CoursesService);
  private inscripcionesService = inject(InscripcionesService);
  private examenesService = inject(ExamenesService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);
  private reproduccionService = inject(ReproduccionService);
  private gptService = inject(GPTAssistantService);

  currentTab = signal<'overview' | 'resources' | 'notes' | 'comments' | 'assistant'>('overview');
  currentLesson = signal(0);
  showNotes = signal(true);
  sidebarOpen = signal(false); // Control del sidebar en móviles
  newComment = signal('');
  userNote = signal('');
  isLoading = signal(false);
  cursoId = signal<number | null>(null);

  // Formulario de subir recurso
  showUploadForm = signal(false);
  isUploadingResource = signal(false);
  uploadForm = {
    nombre_recurso: signal(''),
    tipo_recurso: signal(''),
    descripcion: signal(''),
    orden: signal(1),
    selectedFile: signal<File | null>(null)
  };

  // Recursos subidos por el estudiante
  recursosEstudiante = signal<RecursoLeccion[]>([]);
  isLoadingRecursos = signal(false);

  // Asistente AI
  assistantMessages = signal<ChatMessage[]>([]);
  isAssistantLoading = signal(false);
  assistantError = signal<string | null>(null);
  newAssistantMessage = signal('');
  private shouldScrollAssistant = false;
  private previousModuleId: number | null = null; // Para detectar cambios de módulo

  // Control de reproducción
  videoBlocked = signal(false);
  sesionesActivas = signal(0);
  maxSesiones = signal(2);
  verificandoSesiones = signal(false);

  // Audio Player Controls
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('hlsVideoPlayer') hlsVideoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('youtubeIframe') youtubeIframe?: ElementRef<HTMLIFrameElement>;
  @ViewChild('youtubePlayerShell') youtubePlayerShell?: ElementRef<HTMLDivElement>;
  @ViewChild('embedPlayerContainer') embedPlayerContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('assistantMessagesContainer') assistantMessagesContainer?: ElementRef<HTMLDivElement>;
  isPlaying = signal(false);
  audioCurrentTime = signal(0);
  audioDuration = signal(0);
  audioVolume = signal(1);
  isMuted = signal(false);

  // HLS Player
  hlsPlayer: any = null; // Instancia de HLS.js

  safeVideoUrl: SafeResourceUrl | null = null;
  safeAudioUrl: SafeResourceUrl | null = null;
  safeBlogContent: SafeHtml | null = null;
  safeDescriptionContent: SafeHtml | null = null;
  videoMp4Url: SafeResourceUrl | null = null; // URL del video MP4 cuando viene como ID
  videoHlsUrl: string | null = null; // URL del manifest HLS
  isVideoMp4 = signal(false); // Indica si el video es MP4 o HTML
  isVideoHls = signal(false); // Indica si el video es HLS (.m3u8)
  isVideoYoutube = signal(false);
  youtubeVideoId: string | null = null;
  youtubeEmbedUrl = signal<SafeResourceUrl | null>(null);
  youtubePlaying = signal(false);
  youtubeFullscreen = signal(false);
  private ytPlayer: any = null;
  private readonly onYoutubeFullscreenChange = () => this.syncYoutubeFullscreenState();
  private readonly onYoutubeWindowResize = () => this.scheduleYoutubeLayout();
  private youtubeLayoutTimer: ReturnType<typeof setTimeout> | null = null;

  course = {
    id: 1,
    title: 'Advanced TypeScript Patterns',
    instructor: 'Sarah Johnson',
    progress: 35,
    totalLessons: 12,
    completedLessons: 4,
    isSubscribed: true // true = incluido en suscripción, false = debe comprarlo
  };

  currentLessonData = signal<{
    id: number;
    title: string;
    description: string | null;
    duration: string;
    videoUrl: string;
    audioUrl: string;
    tipo_contenido: string;
    contenido_blog: string | null;
    foto_portada: string | null;
  }>({
    id: 0,
    title: '',
    description: '',
    duration: '',
    videoUrl: '',
    audioUrl: '',
    tipo_contenido: 'video',
    contenido_blog: null,
    foto_portada: null
  });

  courseSections: CourseSection[] = [];

  resources: { id: number; name: string; type: string; size: string; url: string }[] = [];

  comments: Comment[] = [
    {
      id: 1,
      user: 'Carlos Mendoza',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop',
      comment: '¡Excelente explicación! Me ayudó mucho a entender los conceptos básicos.',
      time: 'Hace 2 horas',
      likes: 12
    },
    {
      id: 2,
      user: 'María González',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop',
      comment: 'Muy clara la lección. ¿Podrías profundizar más en los tipos avanzados?',
      time: 'Hace 5 horas',
      likes: 8
    }
  ];

  ngOnInit() {
    window.scrollTo(0, 0);

    // Verificar sesión activa antes de cargar
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
      return;
    }

    // Combinar params y queryParams para evitar carga doble
    // take(1) asegura que solo se ejecute una vez en la carga inicial
    combineLatest([
      this.route.params,
      this.route.queryParams
    ]).pipe(
      take(1) // Solo ejecutar en la primera carga
    ).subscribe(([params, queryParams]) => {
      const id = Number(params['id']);
      const lessonId = queryParams['lesson'] ? Number(queryParams['lesson']) : null;

      console.log('🎬 Cargando curso una sola vez:', { id, lessonId });
      this.cursoId.set(id);
      this.loadCourse(id, lessonId);
    });

    // Escuchar cambios en queryParams DESPUÉS de la carga inicial
    this.route.queryParams.subscribe(queryParams => {
      const lessonId = queryParams['lesson'] ? Number(queryParams['lesson']) : null;

      // Solo cambiar de lección si el curso ya está cargado
      if (this.courseSections.length > 0 && lessonId) {
        console.log('🔄 Cambiando de lección sin recargar:', lessonId);
        this.loadLesson(lessonId);
      }
    });
  }

  setTab(tab: 'overview' | 'resources' | 'notes' | 'comments' | 'assistant') {
    this.currentTab.set(tab);
    if (tab === 'assistant' && this.assistantMessages().length === 0) {
      this.addAssistantWelcomeMessage();
    }
  }

  toggleSection(sectionId: number) {
    const section = this.courseSections.find(s => s.id === sectionId);
    if (section) {
      section.expanded = !section.expanded;
    }
  }

  /**
   * Toggle del sidebar en móviles
   */
  toggleSidebar() {
    this.sidebarOpen.set(!this.sidebarOpen());
  }

  /**
   * Cierra el sidebar (útil al seleccionar una lección en móvil)
   */
  closeSidebar() {
    this.sidebarOpen.set(false);
  }

  /**
   * Carga el curso y sus datos del backend
   */
  loadCourse(courseId: number, lessonId: number | null) {
    this.isLoading.set(true);

    // Primero intentar inscribirse (si ya está inscrito, simplemente continúa)
    this.inscripcionesService.inscribirse(courseId).subscribe({
      next: (inscripcionResponse) => {
        if (inscripcionResponse.ok) {
          console.log('✅ Inscripción exitosa');
        } else if (inscripcionResponse.msg?.includes('Ya estás inscrito')) {
          console.log('ℹ️ Usuario ya inscrito en el curso');
        }

        // Cargar datos del curso, progreso y exámenes en paralelo
        forkJoin({
          curso: this.coursesService.getCursoById(courseId),
          progreso: this.inscripcionesService.getProgresoCurso(courseId),
          examenes: this.examenesService.getExamenesDisponibles(courseId) // Usar getExamenesDisponibles en lugar de getExamenesCurso
        }).subscribe({
          next: ({ curso, progreso, examenes }) => {
            this.isLoading.set(false);

            console.log('📚 [DEBUG] Datos recibidos:', {
              curso: curso.ok,
              progreso: progreso.ok,
              examenes: examenes.ok,
              totalExamenes: examenes.data?.length || 0
            });

            if (curso.ok && curso.data && progreso.ok && progreso.data) {
              let examenesData = examenes.ok && examenes.data ? examenes.data : [];

              // Limpiar duplicados en cada examen antes de mapear
              examenesData = examenesData.map(examen => {
                if (examen.preguntas && examen.preguntas.length > 0) {
                  const preguntasOriginales = examen.preguntas.length;
                  const preguntasLimpias = this.limpiarPreguntasDuplicadas(examen.preguntas);

                  if (preguntasLimpias.length !== preguntasOriginales) {
                    console.warn(`⚠️ [COURSE-PLAYER] Examen ${examen.id_examen} tenía duplicados: ${preguntasOriginales} -> ${preguntasLimpias.length}`);
                  }

                  return { ...examen, preguntas: preguntasLimpias };
                }
                return examen;
              });

              console.log('📝 [DEBUG] Exámenes a mapear (limpios):', examenesData);
              this.mapCourseDataWithProgress(curso.data, progreso.data, examenesData);

              // Si hay un lessonId, cargar esa lección, sino cargar la primera
              if (lessonId) {
                this.loadLesson(lessonId);
              } else {
                this.loadFirstLesson();
              }
            } else {
              this.notificationService.error(
                'Error',
                curso.msg || progreso.msg || 'No se pudo cargar el curso'
              );
            }
          },
          error: (error) => {
            this.isLoading.set(false);
            console.error('Error al cargar curso:', error);
            this.notificationService.error(
              'Error de Conexión',
              'No se pudo conectar con el servidor'
            );
          }
        });
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al inscribirse:', error);
        this.notificationService.error(
          'Error',
          'No se pudo verificar la inscripción'
        );
      }
    });
  }

  /**
   * Mapea los datos del curso con el progreso del usuario y exámenes
   */
  mapCourseDataWithProgress(curso: CursoCompleto, progreso: ProgresoCurso, examenes: Examen[]) {
    console.log('🗺️ [DEBUG] Mapeando curso con exámenes:', {
      totalExamenes: examenes.length,
      examenesPorModulo: examenes.map(e => ({
        id_examen: e.id_examen,
        id_modulo: e.id_modulo,
        titulo: e.titulo,
        preguntas: e.preguntas?.length || 0
      }))
    });

    // Actualizar datos del curso
    this.course = {
      id: curso.id_curso,
      title: curso.titulo,
      instructor: curso.maestro ? `${curso.maestro.nombre} ${curso.maestro.apellido}`.trim() : 'Instructor no disponible',
      progress: progreso.porcentaje_general,
      totalLessons: progreso.total_clases,
      completedLessons: progreso.clases_completadas,
      isSubscribed: true // Si llegó aquí, está inscrito
    };

    // Mapear módulos combinando datos del curso y progreso
    this.courseSections = progreso.modulos
      .sort((a, b) => {
        const moduloA = curso.modulos.find(m => m.id_modulo === a.id_modulo);
        const moduloB = curso.modulos.find(m => m.id_modulo === b.id_modulo);
        return (moduloA?.orden || 0) - (moduloB?.orden || 0);
      })
      .map(moduloProgreso => {
        const moduloCurso = curso.modulos.find(m => m.id_modulo === moduloProgreso.id_modulo);

        // Buscar si este módulo tiene un examen
        const examenModulo = examenes.find(e => e.id_modulo === moduloProgreso.id_modulo);

        console.log(`📋 [DEBUG] Módulo ${moduloProgreso.id_modulo} (${moduloProgreso.titulo_modulo}):`, {
          tieneExamen: !!examenModulo,
          examenId: examenModulo?.id_examen,
          preguntas: examenModulo?.preguntas?.length
        });

        return {
          id: moduloProgreso.id_modulo,
          title: moduloProgreso.titulo_modulo,
          expanded: true,
          lessons: moduloProgreso.clases
            .sort((a, b) => a.orden - b.orden)
            .map(claseProgreso => {
              const claseCurso = moduloCurso?.clases.find(c => c.id_clase === claseProgreso.id_clase);

              return {
                id: claseProgreso.id_clase,
                title: claseProgreso.titulo_clase,
                duration: claseCurso ? this.formatClaseDuration(claseCurso) : 'Sin duración',
                completed: claseProgreso.completada,
                videoUrl: claseCurso?.url_video || '',
                audioUrl: claseCurso?.url_audio || '',
                tipo_contenido: claseCurso?.tipo_contenido || 'video',
                contenido_blog: claseCurso?.contenido_blog || null,
                descripcion: claseCurso?.descripcion || null,
                foto_portada: claseCurso?.foto_portada || null,
                recursos: claseCurso?.recursos || []
              };
            }),
          // Agregar información del examen si existe
          examen: examenModulo ? {
            id: examenModulo.id_examen,
            titulo: examenModulo.titulo,
            descripcion: examenModulo.descripcion,
            tiempo_limite: examenModulo.tiempo_limite,
            intentos_permitidos: examenModulo.intentos_permitidos,
            num_preguntas: examenModulo.preguntas?.length || 0, // Usar optional chaining y fallback
            nota_minima: examenModulo.nota_minima_aprobacion,
            estado: examenModulo.estado
          } : null
        };
      });

    console.log('📊 [DEBUG] Progreso cargado:', {
      total: progreso.total_clases,
      completadas: progreso.clases_completadas,
      porcentaje: progreso.porcentaje_general
    });

    console.log('✅ [DEBUG] Secciones finales con exámenes:',
      this.courseSections.map(s => ({
        id: s.id,
        titulo: s.title,
        tieneExamen: !!s.examen,
        examenId: s.examen?.id
      }))
    );
  }

  /**
   * Formatea la duración de una clase
   */
  formatClaseDuration(clase: Clase): string {
    if (clase.tipo_contenido === 'blog') {
      return clase.tiempo_lectura_minutos ? `${clase.tiempo_lectura_minutos} min lectura` : 'Lectura';
    }

    if (clase.duracion_minutos) {
      const mins = clase.duracion_minutos;
      if (mins >= 60) {
        const hours = Math.floor(mins / 60);
        const remainingMins = mins % 60;
        return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
      }
      return `${mins}m`;
    }

    return 'Sin duración';
  }

  /**
   * Carga la primera lección disponible
   */
  loadFirstLesson() {
    if (this.courseSections.length > 0 && this.courseSections[0].lessons.length > 0) {
      const firstLesson = this.courseSections[0].lessons[0];
      this.selectLesson(firstLesson);
    }
  }

  /**
   * Carga una lección específica por ID
   */
  loadLesson(lessonId: number) {
    const allLessons = this.courseSections.flatMap(s => s.lessons);
    const lesson = allLessons.find(l => l.id === lessonId);

    if (lesson) {
      this.selectLesson(lesson);
    } else {
      console.warn('Lección no encontrada:', lessonId);
      this.loadFirstLesson();
    }
  }

  /**
   * Detecta si el url_video es YouTube (iframe embebido o URL).
   */
  private esVideoYoutube(urlVideo: string): boolean {
    const lower = urlVideo.toLowerCase();
    return (
      lower.includes('youtube.com') ||
      lower.includes('youtu.be') ||
      lower.includes('youtube-nocookie.com')
    );
  }

  /**
   * Extrae el ID de video de YouTube desde iframe HTML o URL.
   */
  private extractYoutubeVideoId(urlVideo: string): string | null {
    const iframeSrc = urlVideo.match(/src=["']([^"']+)["']/i)?.[1];
    const candidate = iframeSrc ?? urlVideo;

    const patterns = [
      /(?:youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/)([\w-]{11})/,
      /youtu\.be\/([\w-]{11})/,
      /[?&]v=([\w-]{11})/,
    ];

    for (const pattern of patterns) {
      const match = candidate.match(pattern);
      if (match?.[1]) return match[1];
    }
    return null;
  }

  /** URL embebida limpia (sin chrome de YouTube), estilo reproductor protegido. */
  private buildYoutubeEmbedUrl(videoId: string): SafeResourceUrl {
    const params = new URLSearchParams({
      autoplay: '0',
      controls: '0',
      modestbranding: '1',
      rel: '0',
      fs: '0',
      disablekb: '1',
      playsinline: '1',
      iv_load_policy: '3',
      cc_load_policy: '0',
      enablejsapi: '1',
      origin: window.location.origin,
      vq: 'hd720',
    });
    const url = `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private activateYoutubeLesson(videoId: string): void {
    this.youtubeVideoId = videoId;
    this.youtubeEmbedUrl.set(this.buildYoutubeEmbedUrl(videoId));
    this.isVideoYoutube.set(true);
    this.safeVideoUrl = null;
    console.log('▶️ YouTube detectado:', videoId);
    setTimeout(() => this.initYoutubePlayer(), 350);
  }
  private buildResponsiveEmbedHtml(html: string): string {
    if (!html.includes('<iframe')) {
      return html;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const iframe = doc.querySelector('iframe');
    if (!iframe) return html;

    iframe.removeAttribute('width');
    iframe.removeAttribute('height');
    iframe.setAttribute('class', 'embed-responsive-iframe');
    iframe.setAttribute('allowfullscreen', 'true');

    return `<div class="embed-video-container">${iframe.outerHTML}</div>`;
  }

  private resetVideoSources(): void {
    this.safeVideoUrl = null;
    this.safeAudioUrl = null;
    this.videoMp4Url = null;
    this.videoHlsUrl = null;
    this.isVideoMp4.set(false);
    this.isVideoHls.set(false);
    this.isVideoYoutube.set(false);
    this.youtubeVideoId = null;
    this.youtubeEmbedUrl.set(null);
    this.youtubePlaying.set(false);
    this.youtubeFullscreen.set(false);
    this.exitYoutubeFullscreenIfActive();
    this.destroyHlsPlayer();
    this.destroyYoutubePlayer();
  }

  private cargarVideoEmbedHtml(html: string): void {
    if (this.esVideoYoutube(html)) {
      const videoId = this.extractYoutubeVideoId(html);
      if (videoId) {
        this.activateYoutubeLesson(videoId);
        return;
      }
    }

    this.safeVideoUrl = this.sanitizer.bypassSecurityTrustHtml(
      this.buildResponsiveEmbedHtml(html)
    ) as SafeHtml;
    console.log('📺 Video HTML embebido (responsive)');
  }

  private cargarVideoSegunTipo(lesson: Lesson): void {
    const url = lesson.videoUrl;

    if (this.esVideoHls(url)) {
      this.videoHlsUrl = this.construirUrlVideoHls(url);
      this.isVideoHls.set(true);
      console.log('🎬 Video HLS detectado:', this.videoHlsUrl);
      setTimeout(() => this.initHlsPlayer(), 300);
      return;
    }

    if (this.esVideoMp4(url)) {
      const videoUrl = this.construirUrlVideoMp4(url);
      this.videoMp4Url = this.sanitizer.bypassSecurityTrustResourceUrl(videoUrl);
      this.isVideoMp4.set(true);
      console.log('🎥 Video MP4 detectado:', videoUrl);
      return;
    }

    if (this.esVideoYoutube(url)) {
      const videoId = this.extractYoutubeVideoId(url);
      if (videoId) {
        this.activateYoutubeLesson(videoId);
        return;
      }
    }

    this.cargarVideoEmbedHtml(url);
  }

  private loadYoutubeApi(): Promise<void> {
    const w = window as typeof window & {
      YT?: { Player: new (...args: unknown[]) => unknown };
      onYouTubeIframeAPIReady?: () => void;
    };

    if (w.YT?.Player) {
      return Promise.resolve();
    }

    if (!CoursePlayerComponent.ytApiLoading) {
      CoursePlayerComponent.ytApiLoading = new Promise((resolve) => {
        const previousReady = w.onYouTubeIframeAPIReady;
        w.onYouTubeIframeAPIReady = () => {
          previousReady?.();
          resolve();
        };

        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(tag);
        }
      });
    }

    return CoursePlayerComponent.ytApiLoading;
  }

  private async initYoutubePlayer(): Promise<void> {
    if (!this.youtubeVideoId || !this.isVideoYoutube()) return;

    await this.loadYoutubeApi();

    const tryInit = (attempt = 0): void => {
      const iframe = this.youtubeIframe?.nativeElement;
      if (!iframe) {
        if (attempt < 15) {
          setTimeout(() => tryInit(attempt + 1), 100);
        }
        return;
      }

      const YT = (window as typeof window & { YT?: { Player: new (...args: unknown[]) => unknown; PlayerState: { PLAYING: number } } }).YT;
      if (!YT?.Player) {
        if (attempt < 15) {
          setTimeout(() => tryInit(attempt + 1), 100);
        }
        return;
      }

      this.destroyYoutubePlayer(false);

      this.ytPlayer = new YT.Player(iframe, {
        events: {
          onReady: (event: { target: { setSize?: (w: number, h: number) => void } }) => {
            this.youtubePlaying.set(false);
            this.applyYoutubeHdQuality(event.target);
          },
          onStateChange: (event: { data: number; target: { setSize?: (w: number, h: number) => void } }) => {
            this.youtubePlaying.set(event.data === YT.PlayerState.PLAYING);
            if (event.data === YT.PlayerState.PLAYING) {
              setTimeout(() => this.applyYoutubeHdQuality(event.target), 300);
            }
          },
          onPlaybackQualityChange: (event: { data: string; target: { setSize?: (w: number, h: number) => void } }) => {
            const lowQualities = ['tiny', 'small', 'medium', 'large'];
            if (lowQualities.includes(event.data)) {
              setTimeout(() => this.applyYoutubeHdQuality(event.target), 500);
            }
          },
        },
      });
    };

    setTimeout(() => tryInit(), 100);
  }

  private destroyYoutubePlayer(clearIframe = true): void {
    if (this.youtubeLayoutTimer) {
      clearTimeout(this.youtubeLayoutTimer);
      this.youtubeLayoutTimer = null;
    }

    if (this.ytPlayer?.destroy) {
      try {
        this.ytPlayer.destroy();
      } catch {
        /* noop */
      }
    }
    this.ytPlayer = null;
    if (clearIframe && this.youtubeIframe?.nativeElement) {
      this.youtubeIframe.nativeElement.src = '';
    }
  }

  toggleYoutubePlayback(): void {
    if (!this.ytPlayer?.getPlayerState) return;
    const YT = (window as typeof window & { YT: any }).YT;
    const state = this.ytPlayer.getPlayerState();
    if (state === YT.PlayerState.PLAYING) {
      this.ytPlayer.pauseVideo();
    } else {
      this.ytPlayer.playVideo();
    }
  }

  seekYoutubeBackward(): void {
    this.seekYoutubeRelative(-CoursePlayerComponent.YOUTUBE_SEEK_SECONDS);
  }

  seekYoutubeForward(): void {
    this.seekYoutubeRelative(CoursePlayerComponent.YOUTUBE_SEEK_SECONDS);
  }

  private seekYoutubeRelative(deltaSeconds: number): void {
    if (!this.ytPlayer?.getCurrentTime || !this.ytPlayer?.seekTo) return;

    const current = Number(this.ytPlayer.getCurrentTime()) || 0;
    const duration = Number(this.ytPlayer.getDuration?.()) || 0;
    let target = current + deltaSeconds;

    if (duration > 0) {
      target = Math.max(0, Math.min(duration, target));
    } else {
      target = Math.max(0, target);
    }

    this.ytPlayer.seekTo(target, true);
  }

  /** Fuerza resolución HD: YouTube usa el tamaño del iframe para elegir calidad. */
  private applyYoutubeHdQuality(player?: { setSize?: (w: number, h: number) => void }): void {
    const { width, height } = this.getYoutubeTargetDimensions();

    try {
      (player ?? this.ytPlayer)?.setSize?.(width, height);
    } catch {
      /* noop */
    }

    const iframe = this.youtubeIframe?.nativeElement;
    if (iframe) {
      iframe.width = String(width);
      iframe.height = String(height);
    }

    this.layoutYoutubeIframe(width, height);
  }

  private getYoutubeTargetDimensions(): { width: number; height: number } {
    const minWidth = CoursePlayerComponent.YOUTUBE_PLAYER_WIDTH;
    const minHeight = CoursePlayerComponent.YOUTUBE_PLAYER_HEIGHT;
    let width = minWidth;
    let height = minHeight;

    const shell = this.youtubePlayerShell?.nativeElement;
    if (shell) {
      const rect = shell.getBoundingClientRect();
      if (rect.width >= minWidth && rect.height >= minHeight) {
        width = Math.min(Math.round(rect.width), 1920);
        height = Math.round((width * 9) / 16);
        const maxHeight = Math.min(Math.round(rect.height), 1080);
        if (height > maxHeight) {
          height = maxHeight;
          width = Math.round((height * 16) / 9);
        }
      }
    }

    return { width, height };
  }

  private scheduleYoutubeLayout(): void {
    if (!this.isVideoYoutube()) return;
    if (this.youtubeLayoutTimer) {
      clearTimeout(this.youtubeLayoutTimer);
    }
    this.youtubeLayoutTimer = setTimeout(() => {
      const { width, height } = this.getYoutubeTargetDimensions();
      this.layoutYoutubeIframe(width, height);
    }, 120);
  }

  private layoutYoutubeIframe(
    baseWidth = CoursePlayerComponent.YOUTUBE_PLAYER_WIDTH,
    baseHeight = CoursePlayerComponent.YOUTUBE_PLAYER_HEIGHT
  ): void {
    const container = this.youtubePlayerShell?.nativeElement?.querySelector(
      '.youtube-player-container'
    ) as HTMLElement | null;
    const iframe = this.youtubeIframe?.nativeElement;
    if (!container || !iframe) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth <= 0 || containerHeight <= 0) return;

    const brandingScale = CoursePlayerComponent.YOUTUBE_BRANDING_SCALE;
    const scale =
      Math.max(containerWidth / baseWidth, containerHeight / baseHeight) * brandingScale;

    iframe.width = String(baseWidth);
    iframe.height = String(baseHeight);
    iframe.style.position = 'absolute';
    iframe.style.width = `${baseWidth}px`;
    iframe.style.height = `${baseHeight}px`;
    iframe.style.left = '50%';
    iframe.style.top = '50%';
    iframe.style.transform = `translate(-50%, -50%) scale(${scale})`;
    iframe.style.transformOrigin = 'center center';
  }

  toggleYoutubeFullscreen(): void {
    this.toggleVideoFullscreen(this.youtubePlayerShell?.nativeElement);
  }

  toggleEmbedFullscreen(): void {
    this.toggleVideoFullscreen(this.embedPlayerContainer?.nativeElement);
  }

  private toggleVideoFullscreen(container?: HTMLElement): void {
    if (!container) return;

    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const el = container as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
    const active = doc.fullscreenElement ?? doc.webkitFullscreenElement;

    if (active === container) {
      void (document.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      return;
    }

    void (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.());
  }

  private syncYoutubeFullscreenState(): void {
    const targets: HTMLDivElement[] = [];
    const shell = this.youtubePlayerShell?.nativeElement;
    const embed = this.embedPlayerContainer?.nativeElement;
    if (shell) targets.push(shell);
    if (embed) targets.push(embed);

    const doc = document as Document & { webkitFullscreenElement?: Element | null };
    const active = doc.fullscreenElement ?? doc.webkitFullscreenElement;
    this.youtubeFullscreen.set(targets.some((target) => target === active));
    this.scheduleYoutubeLayout();
  }

  private exitYoutubeFullscreenIfActive(): void {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const active = doc.fullscreenElement ?? doc.webkitFullscreenElement;
    if (!active) return;

    const targets = [
      this.youtubePlayerShell?.nativeElement,
      this.embedPlayerContainer?.nativeElement,
    ];

    if (targets.some((target) => target === active)) {
      void (document.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
    }
  }

  /**
   * Obtiene la lección actual del sidebar.
   */
  getCurrentLesson(): Lesson | undefined {
    const allLessons = this.courseSections.flatMap(s => s.lessons);
    return allLessons.find(l => l.id === this.currentLessonData().id);
  }

  /**
   * Selecciona y carga una lección.
   */
  selectLesson(lesson: Lesson) {
    // Evitar recarga si es la misma lección
    const currentLessonId = this.currentLessonData().id;
    if (currentLessonId === lesson.id) {
      console.log('⚠️ Misma lección, evitando recarga:', lesson.id);
      return;
    }

    console.log('📖 Cargando lección:', lesson.id, '-', lesson.title);

    // Finalizar sesión de reproducción anterior si existe
    if (this.reproduccionService.tieneSesionActiva()) {
      this.reproduccionService.finalizarReproduccion().subscribe({
        next: () => console.log('✅ Sesión anterior finalizada'),
        error: (error) => console.error('❌ Error al finalizar sesión anterior:', error)
      });
    }

    // Cerrar sidebar en móviles al seleccionar una lección
    this.closeSidebar();

    this.currentLessonData.set({
      id: lesson.id,
      title: lesson.title,
      description: lesson.descripcion,
      duration: lesson.duration,
      videoUrl: lesson.videoUrl,
      audioUrl: lesson.audioUrl,
      tipo_contenido: lesson.tipo_contenido,
      contenido_blog: lesson.contenido_blog,
      foto_portada: lesson.foto_portada
    });

    // Cargar recursos de la lección (solo recursos oficiales, sin id_usuario)
    const recursosOficiales = lesson.recursos.filter(recurso => !recurso.id_usuario);
    const recursosEstudiantes = lesson.recursos.filter(recurso => recurso.id_usuario);

    console.log(`📚 Recursos de la clase ${lesson.id}:`, {
      total: lesson.recursos.length,
      oficiales: recursosOficiales.length,
      estudiantes: recursosEstudiantes.length
    });

    this.resources = recursosOficiales.map(recurso => ({
      id: recurso.id_recurso,
      name: recurso.nombre_recurso,
      type: recurso.tipo_recurso.toUpperCase(),
      size: this.formatFileSize(recurso.tamaño_bytes),
      url: recurso.url_recurso
    }));

    // Cargar recursos subidos por el estudiante
    this.cargarRecursosEstudiante(lesson.id);

    // Sanitizar la descripción si contiene HTML
    if (lesson.descripcion) {
      this.safeDescriptionContent = this.sanitizer.bypassSecurityTrustHtml(lesson.descripcion);
    } else {
      this.safeDescriptionContent = null;
    }

    // Renderizar contenido según tipo
    if (lesson.tipo_contenido === 'blog' && lesson.contenido_blog) {
      this.safeBlogContent = this.sanitizer.bypassSecurityTrustHtml(lesson.contenido_blog);
      this.safeVideoUrl = null;
      this.safeAudioUrl = null;
      this.videoMp4Url = null;
      this.videoHlsUrl = null;
      this.isVideoMp4.set(false);
      this.isVideoHls.set(false);
      this.isVideoYoutube.set(false);
      this.youtubeVideoId = null;
      this.youtubeEmbedUrl.set(null);
      this.destroyYoutubePlayer();
      this.destroyHlsPlayer();
      this.videoBlocked.set(false);
    } else if (lesson.tipo_contenido === 'podcast' && lesson.audioUrl) {
      // Construir URL completa del podcast
      let audioUrl = lesson.audioUrl;
      if (!audioUrl.startsWith('http')) {
        audioUrl = `https://storage.googleapis.com/traidinglab/audios/podcasts/${audioUrl}`;
      }
      this.safeAudioUrl = this.sanitizer.bypassSecurityTrustResourceUrl(audioUrl);
      this.safeVideoUrl = null;
      this.safeBlogContent = null;
      this.videoMp4Url = null;
      this.videoHlsUrl = null;
      this.isVideoMp4.set(false);
      this.isVideoHls.set(false);
      this.isVideoYoutube.set(false);
      this.youtubeVideoId = null;
      this.youtubeEmbedUrl.set(null);
      this.destroyYoutubePlayer();
      this.destroyHlsPlayer();
      this.videoBlocked.set(false);
    } else if (lesson.videoUrl) {
      // Verificar sesiones activas antes de permitir la reproducción
      this.verificarYCargarVideo(lesson);
    }

    // Actualizar URL con el query param de la lección
    // replaceUrl: true evita que cada cambio de lección agregue una entrada al historial
    // Esto previene el bucle al presionar "atrás"
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { lesson: lesson.id },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });

    // Inicializar chat para la nueva clase (limpiar mensajes si cambia de módulo)
    this.initializeChatForLesson(lesson.id);
  }

  /**
   * Inicializa el chat para una lección específica
   */
  private initializeChatForLesson(lessonId: number) {
    const currentModuleId = this.getCurrentModuleId();
    
    // Si cambió de módulo, limpiar la conversación
    if (this.previousModuleId !== null && this.previousModuleId !== currentModuleId) {
      console.log('🔄 Cambió de módulo, limpiando conversación del chat', {
        moduloAnterior: this.previousModuleId,
        moduloActual: currentModuleId,
        clase: lessonId
      });
      this.assistantMessages.set([]);
      this.gptService.clearCurrentConversation();
      this.addAssistantWelcomeMessage();
    } else if (this.assistantMessages().length === 0) {
      // Si no hay mensajes, agregar mensaje de bienvenida
      this.addAssistantWelcomeMessage();
    }
    
    // Actualizar el módulo anterior para la próxima vez
    this.previousModuleId = currentModuleId;
  }

  /**
   * Detecta si el url_video es un video HLS (.m3u8)
   */
  private esVideoHls(urlVideo: string): boolean {
    // Si termina en .m3u8 o contiene manifest.m3u8, es un video HLS
    return urlVideo.includes('.m3u8') || urlVideo.includes('manifest.m3u8');
  }

  /**
   * Detecta si el url_video es un ID MP4 o HTML
   */
  private esVideoMp4(urlVideo: string): boolean {
    // Si termina en .mp4 o .mov y no contiene HTML (como <iframe>), es un ID MP4
    // También detectar si empieza con "videos/clases/" pero NO es HLS
    const esArchivoVideo = (urlVideo.endsWith('.mp4') || urlVideo.endsWith('.mov')) && !urlVideo.includes('<');
    const esRutaRelativa = urlVideo.startsWith('videos/clases/') && !this.esVideoHls(urlVideo);
    return (esArchivoVideo || esRutaRelativa) && !urlVideo.includes('http') && !this.esVideoHls(urlVideo);
  }

  /**
   * Construye la URL completa del video MP4
   */
  private construirUrlVideoMp4(idMp4: string): string {
    // Si ya empieza con "videos/clases/", solo agregar el dominio base
    if (idMp4.startsWith('videos/clases/')) {
      return `https://storage.googleapis.com/traidinglab/${idMp4}`;
    }
    // Si es solo el nombre del archivo, construir la ruta completa
    return `https://storage.googleapis.com/traidinglab/videos/clases/${idMp4}`;
  }

  /**
   * Construye la URL completa del manifest HLS
   */
  private construirUrlVideoHls(rutaHls: string): string {
    // Si ya empieza con "videos/clases/", solo agregar el dominio base
    if (rutaHls.startsWith('videos/clases/')) {
      return `https://storage.googleapis.com/traidinglab/${rutaHls}`;
    }
    // Si es solo el nombre del archivo, construir la ruta completa
    return `https://storage.googleapis.com/traidinglab/videos/clases/${rutaHls}`;
  }

  /**
   * Verifica las sesiones activas antes de cargar el video
   */
  private verificarYCargarVideo(lesson: Lesson) {
    this.verificandoSesiones.set(true);
    this.videoBlocked.set(false);
    this.safeBlogContent = null;
    this.resetVideoSources();

    this.reproduccionService.getSesionesActivas().subscribe({
      next: (response) => {
        this.verificandoSesiones.set(false);

        if (response.ok && response.sesiones) {
          const sesionesActivas = response.total_sesiones || 0;
          const maxSesiones = response.max_sesiones || 2;

          this.sesionesActivas.set(sesionesActivas);
          this.maxSesiones.set(maxSesiones);

          console.log(`📊 Sesiones activas: ${sesionesActivas}/${maxSesiones}`);

          if (sesionesActivas >= maxSesiones) {
            console.warn('🚫 Máximo de sesiones alcanzado, bloqueando video');
            this.videoBlocked.set(true);
            this.notificationService.error(
              'Límite de reproducción alcanzado',
              `Ya tienes ${sesionesActivas} sesión(es) activa(s). Cierra otras sesiones para continuar.`
            );
          } else {
            this.cargarVideoSegunTipo(lesson);
            this.videoBlocked.set(false);
            this.iniciarSesionReproduccion(lesson.id);
          }
        } else {
          console.warn('⚠️ No se pudo verificar sesiones, permitiendo reproducción');
          this.cargarVideoSegunTipo(lesson);
          this.videoBlocked.set(false);
          this.iniciarSesionReproduccion(lesson.id);
        }
      },
      error: (error) => {
        this.verificandoSesiones.set(false);
        const apiNoDisponible = error.status === 0;

        if (apiNoDisponible) {
          console.warn('⚠️ API no disponible; reproduciendo sin control de sesiones');
        } else {
          console.error('❌ Error al verificar sesiones:', error);
          this.notificationService.warning(
            'No se pudo verificar las sesiones',
            'Continuando con la reproducción...'
          );
        }

        this.cargarVideoSegunTipo(lesson);
        this.videoBlocked.set(false);

        if (!apiNoDisponible) {
          this.iniciarSesionReproduccion(lesson.id);
        }
      }
    });
  }

  /**
   * Inicia una sesión de reproducción para el contenido
   */
  private iniciarSesionReproduccion(lessonId: number) {
    this.reproduccionService.iniciarReproduccion('clase', lessonId).subscribe({
      next: (response) => {
        if (response.ok) {
          console.log(`✅ Sesión de reproducción iniciada para clase ${lessonId}`);
          if (response.sesiones_activas && response.max_sesiones) {
            console.log(`📊 Sesiones activas: ${response.sesiones_activas}/${response.max_sesiones}`);
          }
        } else {
          console.warn('⚠️ No se pudo iniciar sesión de reproducción:', response.msg);

          // Verificar si es por límite de sesiones alcanzado
          if (response.code === 'MAX_PLAYBACK_SESSIONS') {
            console.error('🚫 Límite de reproducciones alcanzado');

            // Bloquear el video y mostrar mensaje
            this.videoBlocked.set(true);
            this.safeVideoUrl = null;

            if (response.sesiones_activas !== undefined && response.limite !== undefined) {
              this.sesionesActivas.set(response.sesiones_activas);
              this.maxSesiones.set(response.limite);
            }

            this.notificationService.error(
              'Límite de reproducción alcanzado',
              response.msg || 'Ya tienes el máximo de sesiones activas. Cierra otras sesiones para continuar.'
            );
          } else if (response.msg?.toLowerCase().includes('sesiones') ||
            response.msg?.toLowerCase().includes('límite') ||
            response.msg?.toLowerCase().includes('máximo')) {
            this.notificationService.error(
              'Límite de reproducción',
              response.msg || 'Has alcanzado el máximo de sesiones simultáneas (2). Cierra otras sesiones para continuar.'
            );
          }
        }
      },
      error: (error) => {
        if (error.status === 0) {
          console.warn('⚠️ No se pudo registrar la sesión de reproducción (API no disponible)');
          return;
        }

        console.error('❌ Error al iniciar sesión de reproducción:', error);

        // Verificar si el error tiene la estructura de límite alcanzado
        if (error.error?.code === 'MAX_PLAYBACK_SESSIONS') {
          console.error('🚫 Límite de reproducciones alcanzado (error)');

          // Bloquear el video y mostrar mensaje
          this.videoBlocked.set(true);
          this.safeVideoUrl = null;

          if (error.error.sesiones_activas !== undefined && error.error.limite !== undefined) {
            this.sesionesActivas.set(error.error.sesiones_activas);
            this.maxSesiones.set(error.error.limite);
          }

          this.notificationService.error(
            'Límite de reproducción alcanzado',
            error.error.msg || 'Ya tienes 2 sesiones activas. Cierra otras sesiones para continuar.'
          );
        } else {
          // Mostrar mensaje de error al usuario
          const errorMsg = error.error?.msg || error.message || 'Error al iniciar reproducción';

          if (errorMsg.toLowerCase().includes('sesiones') ||
            errorMsg.toLowerCase().includes('límite') ||
            errorMsg.toLowerCase().includes('máximo')) {
            this.notificationService.error(
              'Límite de reproducción alcanzado',
              'Has excedido el máximo de 2 sesiones de reproducción simultáneas. Por favor, cierra otras sesiones activas para continuar.'
            );
          } else {
            this.notificationService.warning(
              'Advertencia',
              'No se pudo iniciar el control de reproducción, pero puedes continuar viendo el contenido.'
            );
          }
        }
      }
    });
  }

  markAsCompleted() {
    const allLessons = this.courseSections.flatMap(s => s.lessons);
    const lesson = allLessons.find(l => l.id === this.currentLessonData().id);

    if (lesson) {
      const wasCompleted = lesson.completed;

      // Llamar al backend para marcar como completada
      this.inscripcionesService.completarClase(lesson.id).subscribe({
        next: (response) => {
          if (response.ok) {
            // Actualizar estado local
            lesson.completed = !wasCompleted;
            this.updateProgress();

            if (!wasCompleted) {
              this.notificationService.success(
                '¡Clase completada!',
                'Has completado esta lección exitosamente'
              );
            }

            console.log('✅ Clase marcada como completada:', lesson.id);
          } else {
            this.notificationService.error(
              'Error',
              response.msg || 'No se pudo actualizar el progreso'
            );
          }
        },
        error: (error) => {
          console.error('Error al completar clase:', error);
          this.notificationService.error(
            'Error',
            'No se pudo guardar el progreso'
          );
        }
      });
    }
  }

  updateProgress() {
    const allLessons = this.courseSections.flatMap(s => s.lessons);
    const completed = allLessons.filter(l => l.completed).length;
    this.course.completedLessons = completed;
    this.course.progress = Math.round((completed / allLessons.length) * 100);
  }

  postComment() {
    if (this.newComment().trim()) {
      const comment: Comment = {
        id: this.comments.length + 1,
        user: 'Usuario Actual',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop',
        comment: this.newComment(),
        time: 'Ahora',
        likes: 0
      };
      this.comments.unshift(comment);
      this.newComment.set('');
    }
  }

  saveNote() {
    console.log('Nota guardada:', this.userNote());
    // Aquí guardarías la nota en el backend
  }

  /**
   * Descarga un recurso de la lección
   */
  downloadResource(resource: any) {
    let url = resource.url;

    // Si la URL no es completa (no empieza con http), construirla
    if (!url.startsWith('http')) {
      // Si la URL ya incluye la ruta completa (clases/, clases/estudiantes/, etc)
      if (url.includes('/')) {
        url = `https://storage.googleapis.com/traidinglab/recursos/${url}`;
      } else {
        // Si es solo el nombre del archivo, agregar la ruta de recursos del curso
        url = `https://storage.googleapis.com/traidinglab/recursos/clases/${url}`;
      }
    }

    console.log('📥 Descargando recurso:', resource.name, 'desde', url);

    // Abrir en nueva pestaña para descargar
    window.open(url, '_blank');

    this.notificationService.success('Descarga iniciada', `Descargando ${resource.name}`);
  }

  /**
   * Formatea el tamaño de archivo en bytes a KB/MB/GB
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  goBack() {
    const courseId = this.cursoId();
    if (courseId) {
      this.router.navigate(['/course', courseId]);
    } else {
      this.router.navigate(['/dashboard/home']);
    }
  }

  goToExam(examenId: number) {
    this.router.navigate(['/exam', examenId], {
      queryParams: { course: this.course.id }
    });
  }

  isLessonCompleted(): boolean {
    const allLessons = this.courseSections.flatMap(s => s.lessons);
    const lesson = allLessons.find(l => l.id === this.currentLessonData().id);
    return lesson?.completed || false;
  }

  /**
   * Navega a la siguiente lección
   */
  goToNextLesson() {
    const allLessons = this.courseSections.flatMap(s => s.lessons);
    const currentIndex = allLessons.findIndex(l => l.id === this.currentLessonData().id);

    if (currentIndex >= 0 && currentIndex < allLessons.length - 1) {
      const nextLesson = allLessons[currentIndex + 1];
      this.selectLesson(nextLesson);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      this.notificationService.info('Fin del curso', 'Has completado todas las lecciones');
    }
  }

  /**
   * Obtiene la imagen de portada del blog o el gradiente por defecto
   */
  getBlogCoverImage(): string {
    const fotoPortada = this.currentLessonData().foto_portada;

    if (fotoPortada) {
      const imageUrl = `https://storage.googleapis.com/traidinglab/imagenes/clases/${fotoPortada}`;
      return `linear-gradient(rgba(11, 19, 43, 0.3), rgba(11, 19, 43, 0.7)), url('${imageUrl}')`;
    }

    // Gradiente por defecto si no hay imagen
    return 'linear-gradient(135deg, #0e40d8 0%, #1a73e8 50%, #4a9eff 100%)';
  }

  /**
   * Abre el formulario de subir recurso
   */
  abrirFormularioSubida() {
    this.showUploadForm.set(true);
    this.limpiarFormularioSubida();
  }

  /**
   * Cierra el formulario de subir recurso
   */
  cerrarFormularioSubida() {
    this.showUploadForm.set(false);
    this.limpiarFormularioSubida();
  }

  /**
   * Limpia el formulario de subida
   */
  limpiarFormularioSubida() {
    this.uploadForm.nombre_recurso.set('');
    this.uploadForm.tipo_recurso.set('');
    this.uploadForm.descripcion.set('');
    this.uploadForm.orden.set(1);
    this.uploadForm.selectedFile.set(null);
  }

  /**
   * Maneja la selección de archivo
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.uploadForm.selectedFile.set(file);

      // Si no hay nombre de recurso, usar el nombre del archivo
      if (!this.uploadForm.nombre_recurso()) {
        this.uploadForm.nombre_recurso.set(file.name);
      }

      // Auto-detectar el tipo de recurso basado en la extensión (SIEMPRE)
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const tipoMap: { [key: string]: string } = {
        'pdf': 'pdf',
        'doc': 'documento',
        'docx': 'documento',
        'xls': 'hoja de cálculo',
        'xlsx': 'hoja de cálculo',
        'ppt': 'presentación',
        'pptx': 'presentación',
        'zip': 'archivo comprimido',
        'rar': 'archivo comprimido',
        'jpg': 'imagen',
        'jpeg': 'imagen',
        'png': 'imagen',
        'gif': 'imagen',
        'mp4': 'video',
        'mov': 'video',
        'avi': 'video',
        'txt': 'texto',
        'csv': 'hoja de cálculo',
        'js': 'código',
        'ts': 'código',
        'py': 'código',
        'java': 'código',
        'html': 'código',
        'css': 'código'
      };
      this.uploadForm.tipo_recurso.set(tipoMap[extension] || 'archivo');

      console.log('📎 Archivo seleccionado:', {
        nombre: file.name,
        tamaño: file.size + ' bytes',
        tipo: tipoMap[extension] || 'archivo'
      });
    }
  }

  /**
   * Carga los recursos subidos por el estudiante para una clase específica
   */
  cargarRecursosEstudiante(idClase: number) {
    this.isLoadingRecursos.set(true);

    this.coursesService.getMisRecursosClase(idClase).subscribe({
      next: (response) => {
        this.isLoadingRecursos.set(false);

        if (response.ok && response.data) {
          this.recursosEstudiante.set(response.data);
          console.log(`✅ Recursos del estudiante cargados para clase ${idClase}:`, response.data.length);
        } else {
          this.recursosEstudiante.set([]);
        }
      },
      error: (error) => {
        this.isLoadingRecursos.set(false);
        console.error('❌ Error al cargar recursos del estudiante:', error);
        this.recursosEstudiante.set([]);
      }
    });
  }

  /**
   * Sube el recurso del estudiante
   */
  subirRecursoEstudiante() {
    const file = this.uploadForm.selectedFile();
    const nombre = this.uploadForm.nombre_recurso();
    const tipo = this.uploadForm.tipo_recurso(); // Se determina automáticamente al seleccionar archivo
    const descripcion = this.uploadForm.descripcion();

    if (!file) {
      this.notificationService.error('Error', 'Debes seleccionar un archivo');
      return;
    }

    if (!nombre.trim()) {
      this.notificationService.error('Error', 'Debes ingresar un nombre para el recurso');
      return;
    }

    // El tipo ya se determinó automáticamente en onFileSelected()
    // No es necesario validarlo

    this.isUploadingResource.set(true);

    const data = {
      id_clase: this.currentLessonData().id,
      nombre_recurso: nombre,
      tipo_recurso: tipo || 'Archivo', // Fallback por si acaso
      descripcion: descripcion,
      orden: this.uploadForm.orden(),
      file: file
    };

    console.log('📤 Subiendo recurso del estudiante:', data);

    this.coursesService.subirRecursoEstudiante(data).subscribe({
      next: (response) => {
        this.isUploadingResource.set(false);

        if (response.ok) {
          this.notificationService.success(
            'Recurso subido',
            'Tu recurso se ha subido exitosamente'
          );
          this.cerrarFormularioSubida();

          // Recargar los recursos del estudiante para mostrar el recién subido
          this.cargarRecursosEstudiante(this.currentLessonData().id);
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudo subir el recurso'
          );
        }
      },
      error: (error) => {
        this.isUploadingResource.set(false);
        console.error('❌ Error al subir recurso:', error);
        this.notificationService.error(
          'Error',
          'No se pudo conectar con el servidor'
        );
      }
    });
  }

  /**
   * Obtiene el nombre del archivo seleccionado
   */
  getSelectedFileName(): string {
    const file = this.uploadForm.selectedFile();
    return file ? file.name : 'Ningún archivo seleccionado';
  }

  /**
   * Obtiene el tamaño del archivo seleccionado formateado
   */
  getSelectedFileSize(): string {
    const file = this.uploadForm.selectedFile();
    return file ? this.formatFileSize(file.size) : '';
  }

  /**
   * Obtiene el tamaño formateado de un recurso del estudiante
   */
  getRecursoSize(recurso: RecursoLeccion): string {
    return this.formatFileSize(recurso.tamaño_bytes);
  }

  /**
   * Convierte un recurso del estudiante al formato Resource para descargar
   */
  getResourceFromRecurso(recurso: RecursoLeccion): Resource {
    return {
      id: recurso.id_recurso,
      name: recurso.nombre_recurso,
      type: recurso.tipo_recurso,
      size: this.formatFileSize(recurso.tamaño_bytes),
      url: recurso.url_recurso
    };
  }

  /**
   * Limpia preguntas duplicadas de un array
   */
  private limpiarPreguntasDuplicadas(preguntas: any[]): any[] {
    if (!preguntas || preguntas.length === 0) return [];

    const idsVistos = new Set<number>();
    const preguntasUnicas: any[] = [];

    preguntas.forEach((pregunta) => {
      if (!idsVistos.has(pregunta.id_pregunta)) {
        idsVistos.add(pregunta.id_pregunta);
        preguntasUnicas.push(pregunta);
      }
    });

    return preguntasUnicas;
  }

  /**
   * Carga HLS.js dinámicamente
   */
  private async loadHlsLibrary(): Promise<any> {
    if (Hls) {
      console.log('✅ HLS.js ya está cargado');
      return Hls;
    }

    try {
      // Intentar importación dinámica
      console.log('📦 Cargando HLS.js...');
      const hlsModule = await import('hls.js');
      // HLS.js exporta la clase directamente como default
      Hls = hlsModule.default || hlsModule;
      console.log('✅ HLS.js cargado exitosamente:', {
        hasDefault: !!hlsModule.default,
        HlsType: typeof Hls,
        isSupported: Hls?.isSupported
      });
      return Hls;
    } catch (error) {
      console.error('❌ Error al cargar HLS.js con import dinámico:', error);

      // Fallback: intentar cargar desde CDN si está disponible globalmente
      if (typeof (window as any).Hls !== 'undefined') {
        console.log('✅ Usando HLS.js desde window global (CDN)');
        Hls = (window as any).Hls;
        return Hls;
      }

      // Si aún no está disponible, intentar cargar desde CDN dinámicamente
      console.log('📡 Intentando cargar HLS.js desde CDN...');
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
        script.onload = () => {
          if (typeof (window as any).Hls !== 'undefined') {
            Hls = (window as any).Hls;
            console.log('✅ HLS.js cargado desde CDN');
            resolve(Hls);
          } else {
            reject(new Error('HLS.js no disponible después de cargar desde CDN'));
          }
        };
        script.onerror = () => {
          reject(new Error('Error al cargar HLS.js desde CDN'));
        };
        document.head.appendChild(script);
      });

      throw error;
    }
  }

  /**
   * Inicializa el reproductor HLS.js
   */
  private async initHlsPlayer() {
    if (!this.videoHlsUrl) {
      console.warn('⚠️ No se puede inicializar HLS: falta URL');
      return;
    }

    // Esperar a que el ViewChild esté disponible
    if (!this.hlsVideoPlayer || !this.hlsVideoPlayer.nativeElement) {
      console.warn('⚠️ Elemento de video no disponible aún, reintentando...', {
        hlsVideoPlayer: !!this.hlsVideoPlayer,
        nativeElement: !!this.hlsVideoPlayer?.nativeElement
      });
      setTimeout(() => this.initHlsPlayer(), 200);
      return;
    }

    const video = this.hlsVideoPlayer.nativeElement;
    console.log('✅ Elemento de video encontrado:', {
      tagName: video.tagName,
      readyState: video.readyState,
      src: video.src
    });

    // Cargar HLS.js si no está disponible
    try {
      const HlsLib = await this.loadHlsLibrary();

      if (!HlsLib || !HlsLib.isSupported) {
        console.error('❌ HLS.js no está disponible o no es soportado');
        this.notificationService.error(
          'Error de reproducción',
          'El reproductor HLS no está disponible. Por favor, recarga la página.'
        );
        return;
      }

      // Destruir reproductor anterior si existe
      this.destroyHlsPlayer();

      const hlsUrl = this.videoHlsUrl;

      console.log('🎬 Inicializando reproductor HLS:', hlsUrl);
      console.log('🔍 Verificando soporte:', {
        nativeSupport: video.canPlayType('application/vnd.apple.mpegurl'),
        hlsJsSupported: HlsLib.isSupported()
      });

      // Usar HLS.js siempre que esté disponible (mejor control y compatibilidad)
      if (HlsLib.isSupported()) {
        console.log('✅ Usando HLS.js para reproducir video');
        this.hlsPlayer = new HlsLib({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          debug: false,
          maxBufferLength: 30,
          maxMaxBufferLength: 60
        });

        // Cargar el manifest HLS
        console.log('📡 Cargando manifest desde:', hlsUrl);
        this.hlsPlayer.loadSource(hlsUrl);
        this.hlsPlayer.attachMedia(video);

        // Verificar que el video esté listo
        video.addEventListener('error', (e) => {
          console.error('❌ Error en elemento de video:', {
            error: video.error,
            code: video.error?.code,
            message: video.error?.message,
            networkState: video.networkState,
            readyState: video.readyState
          });
        });

        // Manejar eventos de HLS
        this.hlsPlayer.on(HlsLib.Events.MANIFEST_PARSED, () => {
          console.log('✅ Manifest HLS parseado correctamente');
          console.log('📊 Niveles disponibles:', this.hlsPlayer.levels);
          console.log('📊 URL del manifest:', hlsUrl);
        });

        this.hlsPlayer.on(HlsLib.Events.LEVEL_LOADED, (event: any, data: any) => {
          console.log('📦 Nivel cargado:', data);
        });

        // Event listeners adicionales del video
        video.addEventListener('loadstart', () => {
          console.log('🔄 Video: carga iniciada');
        });

        video.addEventListener('loadeddata', () => {
          console.log('✅ Video: datos cargados');
        });

        video.addEventListener('canplay', () => {
          console.log('▶️ Video: listo para reproducir');
        });

        video.addEventListener('playing', () => {
          console.log('▶️ Video: reproduciendo');
        });

        video.addEventListener('waiting', () => {
          console.log('⏳ Video: esperando datos');
        });

        video.addEventListener('stalled', () => {
          console.warn('⚠️ Video: carga estancada');
        });

        this.hlsPlayer.on(HlsLib.Events.ERROR, (event: any, data: any) => {
          console.error('❌ Error en HLS:', {
            type: data.type,
            details: data.details,
            fatal: data.fatal,
            error: data.error
          });

          if (data.fatal) {
            switch (data.type) {
              case HlsLib.ErrorTypes.NETWORK_ERROR:
                console.error('Error de red, intentando recuperar...');
                try {
                  this.hlsPlayer.startLoad();
                } catch (e) {
                  console.error('Error al recuperar:', e);
                }
                break;
              case HlsLib.ErrorTypes.MEDIA_ERROR:
                console.error('Error de media, intentando recuperar...');
                try {
                  this.hlsPlayer.recoverMediaError();
                } catch (e) {
                  console.error('Error al recuperar media:', e);
                  // Si la recuperación falla, intentar recargar
                  this.hlsPlayer.startLoad();
                }
                break;
              default:
                console.error('Error fatal, destruyendo reproductor...');
                this.destroyHlsPlayer();
                this.notificationService.error(
                  'Error de reproducción',
                  'No se pudo reproducir el video. Por favor, intenta recargar la página.'
                );
                break;
            }
          }
        });

        // Fallback: Si HLS.js no funciona, intentar con soporte nativo (Safari)
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('✅ Usando soporte nativo de HLS (Safari)');
        video.src = hlsUrl;
        video.crossOrigin = 'anonymous';

        video.addEventListener('loadedmetadata', () => {
          console.log('✅ Video HLS cargado (nativo)');
        });

        video.addEventListener('error', (e) => {
          console.error('❌ Error en video nativo:', e);
          this.notificationService.error(
            'Error de reproducción',
            'No se pudo cargar el video. Verifica tu conexión e intenta de nuevo.'
          );
        });
      } else {
        console.error('❌ HLS no soportado en este navegador');
        this.notificationService.error(
          'Navegador no compatible',
          'Tu navegador no soporta la reproducción de video HLS. Por favor, usa Chrome, Firefox, Edge o Safari.'
        );
      }
    } catch (error) {
      console.error('❌ Error al inicializar HLS:', error);
      this.notificationService.error(
        'Error de reproducción',
        'No se pudo cargar el reproductor HLS. Por favor, recarga la página.'
      );
    }
  }

  /**
   * Destruye el reproductor HLS.js
   */
  private destroyHlsPlayer() {
    if (this.hlsPlayer) {
      console.log('🧹 Destruyendo reproductor HLS anterior...');
      this.hlsPlayer.destroy();
      this.hlsPlayer = null;
    }
    if (this.hlsVideoPlayer) {
      const video = this.hlsVideoPlayer.nativeElement;
      video.src = '';
      video.load();
    }
  }

  /**
   * Lifecycle: Se ejecuta al destruir el componente
   * Finaliza la sesión de reproducción activa
   */
  ngOnDestroy() {
    console.log('🧹 Limpiando reproductor de curso...');

    document.removeEventListener('fullscreenchange', this.onYoutubeFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onYoutubeFullscreenChange);
    window.removeEventListener('resize', this.onYoutubeWindowResize);
    this.exitYoutubeFullscreenIfActive();

    // Destruir reproductor HLS si existe
    this.destroyHlsPlayer();
    this.destroyYoutubePlayer();

    // Finalizar sesión de reproducción si existe
    if (this.reproduccionService.tieneSesionActiva()) {
      this.reproduccionService.finalizarReproduccion().subscribe({
        next: () => console.log('✅ Sesión de reproducción finalizada al salir'),
        error: (error) => console.error('❌ Error al finalizar sesión al salir:', error)
      });
    }

    // Limpiar estado del servicio
    this.reproduccionService.limpiarEstado();
  }

  ngAfterViewInit() {
    document.addEventListener('fullscreenchange', this.onYoutubeFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onYoutubeFullscreenChange);
    window.addEventListener('resize', this.onYoutubeWindowResize);

    // Si hay un video HLS pendiente de inicializar, hacerlo ahora
    if (this.isVideoHls() && this.videoHlsUrl) {
      setTimeout(() => this.initHlsPlayer(), 300);
    }
  }

  ngAfterViewChecked() {
    if (this.shouldScrollAssistant) {
      this.scrollToBottomAssistant();
      this.shouldScrollAssistant = false;
    }
  }

  private scrollToBottomAssistant() {
    if (this.assistantMessagesContainer) {
      const element = this.assistantMessagesContainer.nativeElement;
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Convertir markdown básico a HTML
   */
  parseMarkdown(text: string): SafeHtml {
    if (!text) return this.sanitizer.sanitize(1, '') as SafeHtml;

    let html = text;

    // Proteger bloques de código
    const codeBlocks: string[] = [];
    html = html.replace(/`([^`]+)`/g, (match, code) => {
      const placeholder = `__CODE_${codeBlocks.length}__`;
      codeBlocks.push(code);
      return placeholder;
    });

    // Negritas
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Restaurar código
    codeBlocks.forEach((code, index) => {
      html = html.replace(`__CODE_${index}__`, `<code style="background: rgba(14,64,216,0.1); padding: 2px 4px; border-radius: 4px; font-family: monospace;">${code}</code>`);
    });

    // Párrafos y saltos de línea
    const paragraphs = html.split(/\n\n+/);
    html = paragraphs.map(para => {
      para = para.trim();
      if (!para) return '';

      // Listas
      if (/^[-*]\s+/.test(para)) {
        const items = para.split(/\n/);
        const listItems = items.map(item => {
          const content = item.replace(/^[-*]\s+/, '').trim();
          return content ? `<li>${content}</li>` : '';
        }).filter(Boolean).join('');
        return `<ul style="margin: 8px 0; padding-left: 20px;">${listItems}</ul>`;
      }

      return `<p style="margin-bottom: 8px;">${para.replace(/\n/g, '<br>')}</p>`;
    }).join('');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // Audio Player Methods
  toggleAudioPlay() {
    if (!this.audioPlayer) return;

    if (this.isPlaying()) {
      this.audioPlayer.nativeElement.pause();
      this.isPlaying.set(false);
    } else {
      this.audioPlayer.nativeElement.play();
      this.isPlaying.set(true);
    }
  }

  seekAudio(event: Event) {
    if (!this.audioPlayer) return;
    const input = event.target as HTMLInputElement;
    const time = parseFloat(input.value);
    this.audioPlayer.nativeElement.currentTime = time;
    this.audioCurrentTime.set(time);
  }

  onAudioTimeUpdate() {
    if (!this.audioPlayer) return;
    this.audioCurrentTime.set(this.audioPlayer.nativeElement.currentTime);
  }

  onAudioLoadedMetadata() {
    if (!this.audioPlayer) return;
    this.audioDuration.set(this.audioPlayer.nativeElement.duration);
  }

  onAudioEnded() {
    this.isPlaying.set(false);
    // Opcional: Autoplay siguiente lección
    // this.goToNextLesson();
  }

  rewindAudio(seconds: number) {
    if (!this.audioPlayer) return;
    this.audioPlayer.nativeElement.currentTime = Math.max(0, this.audioPlayer.nativeElement.currentTime - seconds);
  }

  forwardAudio(seconds: number) {
    if (!this.audioPlayer) return;
    this.audioPlayer.nativeElement.currentTime = Math.min(this.audioDuration(), this.audioPlayer.nativeElement.currentTime + seconds);
  }

  toggleMute() {
    if (!this.audioPlayer) return;
    this.audioPlayer.nativeElement.muted = !this.audioPlayer.nativeElement.muted;
    this.isMuted.set(this.audioPlayer.nativeElement.muted);
  }

  setVolume(event: Event) {
    if (!this.audioPlayer) return;
    const input = event.target as HTMLInputElement;
    const volume = parseFloat(input.value);
    this.audioPlayer.nativeElement.volume = volume;
    this.audioVolume.set(volume);
    if (volume > 0 && this.isMuted()) {
      this.audioPlayer.nativeElement.muted = false;
      this.isMuted.set(false);
    }
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  // AI Assistant Methods
  private addAssistantWelcomeMessage() {
    const welcomeMessage: ChatMessage = {
      role: 'assistant',
      content: `¡Hola! Soy tu asistente de **${this.course.title}**. Estoy aquí para ayudarte con cualquier duda sobre la lección **${this.currentLessonData().title}**. ¿En qué puedo apoyarte hoy?`,
      timestamp: new Date(),
      isNew: false
    };
    this.assistantMessages.set([welcomeMessage]);
  }

  async sendMessageToAssistant() {
    const message = this.newAssistantMessage().trim();
    if (!message || this.isAssistantLoading()) return;

    const userMessageText = message;
    this.newAssistantMessage.set('');
    this.isAssistantLoading.set(true);
    this.assistantError.set(null);

    // Agregar mensaje del usuario localmente
    const userMessage: ChatMessage = {
      role: 'user',
      content: userMessageText,
      timestamp: new Date(),
      isNew: true
    };
    this.assistantMessages.update(msgs => [...msgs, userMessage]);
    this.shouldScrollAssistant = true;

    try {
      const lessonId = this.currentLessonData().id;
      const moduleId = this.getCurrentModuleId();
      const conversationId = this.gptService.getCurrentConversationId();

      console.log('💬 Enviando mensaje al asistente:', {
        mensaje: userMessageText.substring(0, 50) + '...',
        id_modulo: moduleId,
        id_clase: lessonId,
        id_conversacion: conversationId
      });

      const response = await this.gptService.sendMessage(
        userMessageText,
        conversationId,
        moduleId,
        lessonId
      ).toPromise();

      if (response && response.ok && response.mensaje_asistente) {
        const processed = this.gptService.processChatResponse(response);

        if (processed.conversationId) {
          this.gptService.setCurrentConversationId(processed.conversationId);
          console.log('✅ Conversación iniciada/continuada:', processed.conversationId);
        }

        const assistantMsg: ChatMessage = {
          ...processed.assistantMessage,
          isNew: true
        };

        this.assistantMessages.update(msgs => [...msgs, assistantMsg]);
        this.shouldScrollAssistant = true;
      } else {
        throw new Error(response?.msg || 'Error al comunicarse con el asistente');
      }
    } catch (error: any) {
      console.error('❌ Error en chat del asistente:', error);
      this.assistantError.set(error.error?.msg || error.message || 'Error al enviar mensaje');

      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.',
        timestamp: new Date(),
        isNew: true
      };
      this.assistantMessages.update(msgs => [...msgs, errorMsg]);
      this.shouldScrollAssistant = true;
    } finally {
      this.isAssistantLoading.set(false);
    }
  }

  private getCurrentModuleId(): number {
    const lessonId = this.currentLessonData().id;
    for (const section of this.courseSections) {
      if (section.lessons.some(l => l.id === lessonId)) {
        return section.id; // El ID de la sección es el id_modulo
      }
    }
    // Si no se encuentra, retornar 0 como fallback (el backend lo manejará)
    console.warn('⚠️ No se encontró el módulo para la lección:', lessonId);
    return 0;
  }

  onAssistantKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessageToAssistant();
    }
  }

  clearAssistantChat() {
    this.assistantMessages.set([]);
    this.gptService.clearCurrentConversation();
    this.addAssistantWelcomeMessage();
    console.log('🧹 Chat del asistente limpiado');
  }
}

